import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faLink,
  faRefresh,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { getRequest, postRequest } from "../../utils/axios";
import { NotificationManager } from "react-notifications";
import moment from "moment";
import { getCurrentUser } from "../../utils/utils";

import MainA from "../../layout/Main Admin";
import Button from "../../components/atoms/Button";
import Loader from "../../components/atoms/Loader";
import Table from "../../components/atoms/Table";
import Input from "../../components/atoms/Input";
import Modal from "../../components/atoms/Modal";

import "./styles.scss";
import Anchor from "../../components/atoms/Anchor";

const baseUrl = "https://pikusports.com/form/";

const FormGroup = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState([]);
  const [sortColumn, setSortColumn] = useState("requestType");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [groups, setGroups] = useState([]);
  const [forms, setForms] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState(null);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [addGroupLoading, setAddGroupLoading] = useState(false);

  const [pageName, setPageName] = useState("Unknown Page");

  const fetchPageName = async () => {
    try {
      if (!eventId) return;
      const resp = await getRequest(`Page/GetPageById/${eventId}`);
      if (resp?.status === 200 && resp.data) {
        setPageName(resp.data.pageName || resp.data.name || "Unknown Page");
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchRequests();
    getGroups();
    getForms();
    fetchPageName();
  }, [eventId]);

  const fetchRequests = async () => {
    try {
      setLoading(true);

      const requestResp = await getRequest(`FormGroup/GetByEvent/${eventId}`);

      const data = requestResp.data || [];

      const sortedData = data.sort((a, b) => {
        if (!a.groupId && b.groupId) return -1;
        if (a.groupId && !b.groupId) return 1;
        return 0;
      });

      setRequestData(sortedData);
      setFilteredRequests(sortedData);
      setSelectedFormId(data[0]?.formId || '');
    } catch (error) {
      console.error(error);
      NotificationManager.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const getGroups = async () => {
    try {
      const resp = await getRequest(`Group/GetGroups`);

      if (resp?.status === 200) {
        setGroups(resp.data);
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to fetch groups");
    }
  };

  const getForms = async () => {
    try {
      const resp = await getRequest(`Form/GetForms`);

      if (resp?.status === 200) {
        setForms(resp.data);
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to fetch forms");
    }
  };

  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";

    setSortColumn(column);
    setSortOrder(order);

    const sorted = [...requestData].sort((a, b) => {
      const aVal = a[column] || "";
      const bVal = b[column] || "";
      return order === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

    setRequestData(sorted);
  };

  const applyFilters = (text) => {
    let data = [...requestData];

    if (text) {
      const lower = text.toLowerCase();

      data = data.filter((item) => {
        return item.groupId
          ? groups
              .find((group) => group.groupId === item.groupId)
              ?.groupName?.toLowerCase()
              .includes(lower)
          : "solo".includes(lower) || item.formId
            ? forms
                .find((form) => form.formId === item.formId)
                ?.formName?.toLowerCase()
                .includes(lower)
            : "solo"?.toLowerCase().includes(lower);
      });
    }

    setFilteredRequests(data);
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    applyFilters(value);
  };

  const handleDeleteModalOpen = (formGroupId) => {
    setDeleteRequestId(formGroupId);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await getRequest(`FormGroup/DeleteFormGroup/${deleteRequestId}`);
      NotificationManager.success("Group deleted successfully");
      fetchRequests();
    } catch (error) {
      console.error(error);
      NotificationManager.error("Failed to delete group");
    } finally {
      setDeleteLoading(false);
      setDeleteModalOpen(false);
    }
  };

  return (
    <MainA className="form-group">
      {loading && <Loader loading={loading} />}

      <Modal
        title="Delete Group"
        isOpen={deleteModalOpen}
        closeModal={() => setDeleteModalOpen(false)}
      >
        <div className="modal-content">
          <p>Are you sure you want to delete this group from the form?</p>
          <div className="modal-actions">
            <Button
              title="Delete"
              onClick={handleDelete}
              loading={deleteLoading}
              className="delete-btn"
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
            <Button
              title="Cancel"
              onClick={() => setDeleteModalOpen(false)}
              className="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <div className="gallery-wrapper">
        <div className="event-header">
          <div className="title">
            <h1>Form Group</h1>
            <p>{pageName}</p>
          </div>

          <div className="filter">
            <Button
              title="Refresh"
              onClick={fetchRequests}
              className="refresh-btn"
            >
              <FontAwesomeIcon icon={faRefresh} />
            </Button>
            {/* <Button
              title="Add Group"
              onClick={() => { setShowAddGroupModal(true); setSelectedGroupIds([]); }}
              className="add-group-btn"
            >
              Add Group
            </Button> */}
            <Input
              className="search-input"
              type="search"
              placeholder="Search"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />

            <Button
              onClick={() => navigate(-1)}
              className="back-btn"
              title="Back"
            >
              Back
            </Button>
          </div>
        </div>
        <div className="requests-table">
          <Table
            pagination={true}
            pageSize={10}
            totalRows={filteredRequests.length}
            categoryName="Form Groups"
          >
            <thead>
              <tr>
                <th>Group</th>
                <th>Form</th>
                <th>Url</th>
                <th>Group Admin</th>
                <th style={{ textAlign: "center" }}>Responses</th>
                {/* <th>Actions</th> */}
              </tr>
            </thead>

            <tbody>
              {filteredRequests.length ? (
                filteredRequests.map((item) => {
                  const group = groups.find(
                    (group) => group.groupId === item.groupId,
                  );
                  return (
                    <tr
                      key={item.formGroupId}
                      className={`${item.groupId ? "" : "solo-group"}`}
                    >
                      <td>{group ? group.groupName : "-"}</td>
                      <td>
                        {item.formId
                          ? forms.find((form) => form.formId === item.formId)
                              ?.name
                          : "-"}
                      </td>
                      <td>
                        {group?.uniqueKey ?
                          <div className="action-btns-2">
                            <a
                              href={`${baseUrl}${group.uniqueKey}?${group.groupName.replace(" ","_")}`}
                              target="_blank"
                            >
                              {baseUrl}
                              {group.uniqueKey}?{group.groupName.replace(" ","_")}
                            </a>
                            <Button
                              title="Copy URL"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${baseUrl}${group.uniqueKey}?${group.groupName.replace(" ","_")}`,
                                );
                                NotificationManager.success(
                                  "URL copied to clipboard",
                                );
                              }}
                              className="copy-btn"
                            >
                              <FontAwesomeIcon icon={faCopy} />
                            </Button>
                            <Button
                            title="Open URL"
                            onClick={() =>
                              window.open(
                                `${baseUrl}${group.uniqueKey}`,
                                "_blank",
                              )
                            }
                            className="open-btn"
                          >
                            <FontAwesomeIcon icon={faLink} />
                          </Button>
                          </div>
                        : "-"}
                      </td>
                      <td>
                        {group?.adminEmails
                          ? group.adminEmails.split(",").map((email, index, arr) => (
                              <span key={index}>
                                <a href={`mailto:${email.trim()}`}>{email.trim()}</a>
                                {index < arr.length - 1 && ", "}
                              </span>
                            ))
                          : "-"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {item?.formResponse.length > 0 ? (
                          <Anchor
                            to={`/form-responses-group/${item.formGroupId}?pageName=${pageName}&groupName=${item.groupId ? groups.find((group) => group.groupId === item.groupId)?.groupName : "-"}`}
                          >
                            {item?.formResponse.length}
                          </Anchor>
                        ) : (
                          0
                        )}
                      </td>
                      {/* <td>
                        <div className="action-btns">
                          {item.groupId && item?.formResponse.length === 0 && (
                            <Button
                              title=" Delete"
                              onClick={() =>
                                handleDeleteModalOpen(item.formGroupId)
                              }
                              className="delete-btn"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          )}
                        </div>
                      </td> */}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      {/* Add Groups to Form Modal */}
      <Modal
        title={"Add Groups To Form"}
        isOpen={showAddGroupModal}
        closeModal={() => setShowAddGroupModal(false)}
      >
        <div className="modal-content">

          <div style={{ marginTop: 12 }}>
            <label>Select Groups</label>
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #eee', padding: 8 }}>
              {(() => {
                const filteredGroups = Array.isArray(groups)
                  ? groups.filter(
                      g =>
                        !requestData.some(r => r.groupId === g.groupId) &&
                        g.isActive
                    )
                  : [];

                return filteredGroups.length > 0 ? (
                  filteredGroups.map(g => {
                    const checked = selectedGroupIds.includes(g.groupId);
                    return (
                      <div
                        key={g.groupId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '4px 0'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked)
                              setSelectedGroupIds(prev => [...prev, g.groupId]);
                            else
                              setSelectedGroupIds(prev =>
                                prev.filter(id => id !== g.groupId)
                              );
                          }}
                        />
                        <span>{g.groupName || g.name}</span>
                      </div>
                    );
                  })
                ) : (
                  <div>No groups available</div>
                );
              })()}
            </div>
          </div>

          <div className="modal-actions" style={{ marginTop: 12 }}>
            <Button title="Add" onClick={async () => {
              if (!selectedFormId) { NotificationManager.error('Please select a form'); return; }
              if (!selectedGroupIds.length) { NotificationManager.error('Please select at least one group'); return; }
              setAddGroupLoading(true);
              try {
                const payloads = selectedGroupIds.map(gid => ({ formId: Number(selectedFormId), groupId: gid, eventId: Number(eventId) }));
                const results = await Promise.all(payloads.map(p => postRequest('FormGroup/CreateFormGroup', p)));
                const ok = results.every(r => r && (r.status === 200 || r.status === 201));
                if (ok) {
                  NotificationManager.success('Groups added to form');
                  setShowAddGroupModal(false);
                  setSelectedGroupIds([]);
                  fetchRequests();
                } else {
                  NotificationManager.error('Failed to add some groups');
                }
              } catch (err) {
                console.error(err);
                NotificationManager.error('Failed to add groups');
              } finally {
                setAddGroupLoading(false);
              }
            }} loading={addGroupLoading}>
              {addGroupLoading ? 'Adding...' : 'Add'}
            </Button>
            <Button title="Cancel" onClick={() => setShowAddGroupModal(false)}>Cancel</Button>
          </div>
        </div>
      </Modal>
    </MainA>
  );
};

export default FormGroup;
